// Libs
import React, { useState, useRef, useEffect, useContext } from 'react';
import styled, { css, ThemeContext } from 'styled-components';
import { useQuery, useMutation } from '@apollo/client';
import { useTranslation } from 'react-i18next';

// Queries
import {
  GET_GLOBAL_INSPECTION_CATEGORIES,
  GET_ALL_MARKETS,
  GET_ALL_GLOBAL_INSPECTION_TEMPLATES
} from '../../api/queries/queries_local';
import {
  CREATE_GLOBAL_INSPECTION_TYPE,
  DUPLICATE_INSPECTION_TYPE,
  EDIT_GLOBAL_INSPECTION_TYPE
} from '../../api/mutations/inspectionTemplate.query';

// Components
import {
  TextInput,
  TextArea,
  Button,
  Recurrence,
  RECURRENCE_DISPLAY_TYPES,
  createRecurrenceValue,
  Chip,
  FileUpload,
  SearchableSingleSelect,
  checkDocumentTypeAndRenderIcon,
  Checkbox
} from '@ibinder/common-frontend';
import InspectionCategoryDropdown from './InspectionCategoryDropdown';

// Utils
import { inspectionTypeSchema } from './InspectionTypeValidation';
import { getRecurrenceUnitOptions } from './inspectionTypeTransformations';
import { FORM_MODES } from './InspectionTypeTemplates';
import { iBinderHeaderEnum, Region } from '@ibinder/ui-common-models';
import { v4 as uuid } from 'uuid';

// Styles
const Wrapper = styled.div`
  height: calc(90vh - 6rem); /* Height of modal, minus header and footer */
  display: flex;
  flex-direction: column;
`;

const FormContainer = styled.div`
  overflow-y: scroll;
  overflow-x: hidden;
  padding: 0 1rem;

  > * {
    margin-bottom: 1rem;
    margin-left: 0;
  }
`;

const Label = styled.h4`
  color: ${(p) => p.theme.colors.discrete};
  font-size: ${(p) => p.theme.fontStyles.smallBoldFont.fontSize}px;
  font-weight: ${(p) => p.theme.fontStyles.smallBoldFont.fontWeight};
  margin-top: 2rem;
  margin-bottom: 1rem;

  &:first-child {
    margin-top: 0;
  }
`;

const SubLabel = styled.h5<{ required?: boolean }>`
  font-weight: ${(p) => p.theme.fontStyles.smallBoldFont.fontWeight};
  ${({ required }) =>
    required &&
    css`
      &:after {
        content: ' *';
        color: ${(p) => p.theme.colors.error};
      }
    `}
`;

const DocumentChip = styled(Chip)`
  svg {
    width: 36px;
  }
`;

const Footer = styled.footer`
  display: flex;
  justify-content: space-between;
  padding-top: 1rem;
  border-top: 1px solid;
  border-color: ${(p) => p.theme.colors.discrete30};
  margin-top: 1rem;

  > * {
    width: auto;
  }
`;

const ErrorField = styled.p`
  color: ${(p) => p.theme.colors.error};
`;

// Inits
const initialSelectedMarket = {
  id: null,
  name: null,
  country: null
};

const recurrenceLabels = {
  legend: 'Recurrences every',
  number: 'Number of',
  unit: 'Time unit/s',
  specify: 'Specify frequency',
  norepeat: 'No recurrences'
};

const recurrenceWindowLabels = {
  earliest: 'Earliest date',
  latest: 'Latest date',
  dateheader: 'Inspection date - is not avaliable anymore',
  before: 'before',
  after: 'after',
  legend: 'Set reminder before duedate'
};

const INITIAL_RECURRENCE_VALUE = 'P1M';
const INITIAL_RECURRENCE_WINDOW_VALUE = 'P1W';

interface InspecionTypeFormProps {
  onClose: any;
  inspectionType?: any;
  mode?: FORM_MODES;
  isCompliant?: boolean;
}

const InspectionTypeForm = ({
  onClose,
  inspectionType = null,
  mode,
  isCompliant = false
}: InspecionTypeFormProps) => {
  const theme = useContext(ThemeContext);
  const { t } = useTranslation();
  const userId = localStorage.getItem('userId');
  const formRef = useRef(null);
  const recurrenceUnitOptions = getRecurrenceUnitOptions(t);
  const editMode = mode === FORM_MODES.EDIT;
  const duplicateMode = mode === FORM_MODES.DUPLICATE;
  const useExistingMode =
    mode === FORM_MODES.EDIT || mode === FORM_MODES.DUPLICATE;
  /* Component States */
  const [typeName, setTypeName] = useState(editMode ? inspectionType.name : '');
  const [typeDesc, setTypeDesc] = useState(
    editMode ? inspectionType.description : ''
  );
  const [compatible, setCompatible] = useState({
    appCompatible: editMode ? inspectionType.appCompatible : false,
    propertyStructureCompatible: editMode
      ? inspectionType.propertyStructureCompatible
      : false
  });
  const [recurrenceOptions, setRecurrenceOptions] = useState(
    editMode ? inspectionType.showRecurrenceOptions : true
  );
  const [reminderOptions, setReminderOptions] = useState(
    editMode ? inspectionType.showReminderOptions : true
  );

  const [selectedInspectionCategory, setSelectedInspectionCategory] =
    useState(null);
  const [selectedMarket, setSelectedMarket] = useState(initialSelectedMarket);
  const [inspectionInterval, setInspectionInterval] = useState(
    useExistingMode
      ? inspectionType.inspectionInterval
      : INITIAL_RECURRENCE_VALUE
  );
  const [displayType, setDisplayType] = useState(
    useExistingMode
      ? inspectionType.inspectionInterval
        ? RECURRENCE_DISPLAY_TYPES.specify
        : RECURRENCE_DISPLAY_TYPES.norepeat
      : RECURRENCE_DISPLAY_TYPES.specify
  );
  const [reminderDisplayType, setReminderDisplayType] = useState(
    useExistingMode
      ? inspectionType.remindBeforeDueDate
        ? RECURRENCE_DISPLAY_TYPES.specify
        : RECURRENCE_DISPLAY_TYPES.norepeat
      : RECURRENCE_DISPLAY_TYPES.specify
  );
  const [recurrenceWindow, setRecurrenceWindow] = useState(
    useExistingMode
      ? inspectionType.remindBeforeDueDate
      : INITIAL_RECURRENCE_WINDOW_VALUE
  );

  const [leniencyFiles, setLeniencyFiles] = useState(
    useExistingMode ? inspectionType.leniencyDocuments : []
  );
  const [isUploadingLeniency, setIsUploadingLeniency] = useState([]);
  const [isUploadingDocs, setIsUploadingDocs] = useState([]);
  const [documents, setDocuments] = useState(
    useExistingMode ? inspectionType.additionalDocuments : []
  );
  const [validationErrors, setValidationErrors] = useState([]);
  const [hasSubmitted, setHasSubmitted] = useState(false);

  const selectedInspectionTypeId = useExistingMode && inspectionType.id;

  function getRegionHeaders() {
    if (isCompliant)
      return {
        [iBinderHeaderEnum.TENANTID]: '', // Needs to be empty string to override automatic set tenantId
        [iBinderHeaderEnum.REGION]: Region.Eu
      };
    return {
      [iBinderHeaderEnum.REGION]: Region.International
    };
  }

  const marketInfo = useQuery(GET_ALL_MARKETS, {
    fetchPolicy: 'no-cache',
    context: {
      headers: getRegionHeaders()
    }
  });

  const inspectionCategories = useQuery(GET_GLOBAL_INSPECTION_CATEGORIES, {
    variables: {
      market: selectedMarket.country?.countryCode
    },
    fetchPolicy: 'no-cache',
    notifyOnNetworkStatusChange: true,
    skip: !selectedMarket.country,
    context: {
      headers: getRegionHeaders()
    }
  });

  /* Prepopulate drop downs when data is available */
  useEffect(() => {
    if (
      inspectionCategories?.data?.inspectionCategories &&
      selectedMarket.country &&
      useExistingMode
    ) {
      setSelectedInspectionCategory(inspectionType.inspectionCategory.id);
    }
  }, [inspectionCategories.data, selectedMarket.country]);

  useEffect(() => {
    if (marketInfo?.data?.get && useExistingMode) {
      setSelectedMarket(
        marketInfo.data.get.find(
          ({ country }) => country.countryCode === inspectionType.market
        )
      );
    }
  }, [marketInfo.data]);
  /* Mutation */
  const [createInspectionType] = useMutation(CREATE_GLOBAL_INSPECTION_TYPE, {
    onCompleted: onClose
  });
  const [editInspectionType] = useMutation(EDIT_GLOBAL_INSPECTION_TYPE, {
    onCompleted: onClose
  });
  const [duplicateInspectionType] = useMutation(DUPLICATE_INSPECTION_TYPE, {
    onCompleted: onClose
  });

  /* Interaction handlers */
  const handleNameChange = ({ target }) => {
    setTypeName(target.value);
  };

  const handleDescChange = ({ target }) => {
    setTypeDesc(target.value);
  };

  const onInspectionIntervalChange = ({ displayType, value }) => {
    /* Complex situation for edit mode and switching type */
    if (
      useExistingMode &&
      displayType === RECURRENCE_DISPLAY_TYPES.specify &&
      !recurrenceWindow
    ) {
      setRecurrenceWindow(INITIAL_RECURRENCE_WINDOW_VALUE);
    }
    setInspectionInterval(value);
    setDisplayType(displayType);
  };

  const onInspectionWindowChange = ({ displayType, value }) => {
    if (
      useExistingMode &&
      displayType === RECURRENCE_DISPLAY_TYPES.specify &&
      !recurrenceWindow
    ) {
      setRecurrenceWindow(INITIAL_RECURRENCE_WINDOW_VALUE);
    }
    setRecurrenceWindow(value);
    setReminderDisplayType(displayType);
  };

  const onLeniencyFileSelectChange = async (e) => {
    const newLeniencyFiles = Array.from(e.target.files).map((file) => {
      return { file, tempId: uuid() };
    });

    setLeniencyFiles([...leniencyFiles, ...newLeniencyFiles]);
    e.target.value = null;
  };

  const popLeniencyFile = (docTempId) => {
    setLeniencyFiles(
      [...leniencyFiles].filter((doc) => doc.tempId !== docTempId)
    );
  };

  const onDocumentSelectChange = async (e) => {
    const newDocuments = Array.from(e.target.files).map((file) => {
      return { file, tempId: uuid() };
    });

    setDocuments([...documents, ...newDocuments]);
    e.target.value = null;
  };

  const popDocument = (docTempId) => {
    setDocuments([...documents].filter((doc) => doc.tempId !== docTempId));
  };

  const uploadFiles = async (docs) => {
    const promises = docs.map(async (doc) => {
      if (!doc.file) {
        return doc;
      }
      const data = await putUploadS3Blob(doc.file);
      return { name: doc?.file?.name || doc.name, id: data.uploadId };
    });
    return await Promise.all(promises);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    setHasSubmitted(true);
    const inspectionCategory =
      inspectionCategories?.data?.inspectionCategories.find(
        ({ id }) => id === selectedInspectionCategory
      );
    const postBody: any = {
      name: typeName,
      description: typeDesc,
      inspectionCategory: {
        Id: inspectionCategory?.id,
        Name: inspectionCategory?.name
      },
      disabled: false,
      appCompatible: compatible.appCompatible,
      propertyStructureCompatible: compatible.propertyStructureCompatible,
      showRecurrenceOptions: recurrenceOptions,
      showReminderOptions: reminderOptions,
      market: selectedMarket?.country?.countryCode,
      inspectionInterval:
        recurrenceOptions && inspectionInterval
          ? createRecurrenceValue(inspectionInterval)
          : null,
      remindBeforeDueDate:
        reminderOptions && inspectionInterval && recurrenceWindow
          ? createRecurrenceValue(recurrenceWindow)
          : null,
      supervisorRole: null,
      leniencyDocuments: await uploadFiles(leniencyFiles),
      additionalDocuments: await uploadFiles(documents)
    };
    try {
      await inspectionTypeSchema.validate(postBody, {
        abortEarly: false
      });
    } catch (e) {
      setHasSubmitted(false);
      setValidationErrors(e.errors);
      formRef.current.scrollTop = 0;
      return;
    }

    setValidationErrors([]);

    if (editMode) {
      editInspectionType({
        variables: {
          input: postBody,
          inspectionTypeId: selectedInspectionTypeId
        },
        context: {
          headers: {
            ...getRegionHeaders(),
            authorization: `Bearer ${localStorage.getItem('access_token')}`
          }
        },
        refetchQueries: [GET_ALL_GLOBAL_INSPECTION_TEMPLATES],
        onCompleted: () => {
          setHasSubmitted(false);
          onClose();
        },
        onError: () => {
          setHasSubmitted(false);
        }
      });
    } else if (duplicateMode) {
      duplicateInspectionType({
        variables: {
          input: postBody,
          inspectionTypeId: selectedInspectionTypeId
        },
        context: {
          headers: {
            ...getRegionHeaders(),
            authorization: `Bearer ${localStorage.getItem('access_token')}`
          }
        },
        refetchQueries: [GET_ALL_GLOBAL_INSPECTION_TEMPLATES],
        onCompleted: () => {
          setHasSubmitted(false);
          onClose();
        },
        onError: () => {
          setHasSubmitted(false);
        }
      });
    } else {
      createInspectionType({
        variables: {
          input: postBody
        },
        context: {
          headers: {
            ...getRegionHeaders(),
            authorization: `Bearer ${localStorage.getItem('access_token')}`
          }
        },
        refetchQueries: [GET_ALL_GLOBAL_INSPECTION_TEMPLATES],
        onCompleted: () => {
          setHasSubmitted(false);
          onClose();
        },
        onError: () => {
          setHasSubmitted(false);
        }
      });
    }
  };

  const putUploadS3Blob = async (file) => {
    const uploadRespons = await fetch(
      '/api/documents/uploads/personalstorage/presignedupload',
      {
        mode: 'cors',
        method: 'GET',
        headers: {
          ...getRegionHeaders(),
          authorization: `Bearer ${localStorage.getItem('access_token')}`
        }
      }
    );

    const uploadResponsJson = await uploadRespons.json();

    const getUploadHeaders = () => {
      if (isCompliant)
        return {
          'Content-Type': '',
          'Content-Length': file.file?.size || file.size
        };
      return {
        'Content-Type': file.file?.type || file.type,
        'Content-Length': file.file?.size || file.size,
        'x-ms-blob-type': 'BlockBlob'
      };
    };

    const fileUploaded = await fetch(uploadResponsJson.presignedUrl, {
      mode: 'cors',
      method: 'PUT',
      headers: getUploadHeaders(),
      body: file
    });

    if (!fileUploaded.ok) {
      console.error('File upload failed', fileUploaded);
      return null;
    }
    return uploadResponsJson;
  };

  return (
    <Wrapper>
      <FormContainer ref={formRef}>
        <Label>Type</Label>

        {/* TODO: Connect validation to input fields, instead of long list */}
        {validationErrors.map((validationError, i) => (
          <ErrorField key={i}>{validationError}</ErrorField>
        ))}

        <TextInput
          label={t('Name of inspection type')}
          value={typeName}
          placeholder={t('Name')}
          changeValue={handleNameChange}
          required={true}
        />

        <TextArea
          label={t('Description')}
          value={typeDesc}
          placeholder={t('Description')}
          changeValue={handleDescChange}
          required={true}
        />

        {marketInfo?.data?.get && (
          <SearchableSingleSelect
            activeKey={selectedMarket.id}
            changeActiveItem={(index) => {
              setSelectedMarket(
                marketInfo?.data?.get.find(({ id }) => id === index)
              );
            }}
            data={marketInfo?.data?.get.map((item) => ({
              Value: item.name,
              Key: item.id
            }))}
            label={t('Market')}
            dropdownText={t('Select')}
            searchPlaceholderText={`${t('Search')} ${t('market')}`}
            required={true}
          />
        )}

        {inspectionCategories?.data?.inspectionCategories && (
          <InspectionCategoryDropdown
            labels={{
              label: 'Inspection Category',
              select: 'Select',
              footerRightBtn: 'Done',
              createNewCategory: 'Create new category'
            }}
            userId={userId}
            activeKey={selectedInspectionCategory}
            data={inspectionCategories?.data?.inspectionCategories.map(
              (item) => ({
                Value: item.name,
                Key: item.id
              })
            )}
            onSelect={(id) => {
              setSelectedInspectionCategory(id);
            }}
            market={selectedMarket.country?.countryCode}
            isCompliant={isCompliant}
          />
        )}

        <Label>{t('Documents')}</Label>
        <SubLabel>{t('Leniency requirements')}</SubLabel>
        <FileUpload
          {...{
            onChange: onLeniencyFileSelectChange,
            label: t('Add leniency requirements'),
            multiple: true
          }}
        />
        {isUploadingLeniency.map((doc) => (
          <Chip
            key={`doc-chip-${doc.tempId || doc.id}`}
            label={doc}
            loading={true}
          />
        ))}
        {leniencyFiles.length > 0 && <h5>Added leniency requirements</h5>}
        {leniencyFiles.map((doc) => (
          <DocumentChip
            key={`doc-chip-${doc.tempId || doc.id}`}
            label={doc?.file?.name || doc.name}
            onClick={(e) => e.preventDefault()}
            onClose={() => popLeniencyFile(doc.tempId || doc.id)}
            icon={checkDocumentTypeAndRenderIcon(
              doc?.file?.name || doc.name,
              theme
            )}
          />
        ))}

        <SubLabel>{t('Additional documents')}</SubLabel>
        <FileUpload
          {...{
            onChange: onDocumentSelectChange,
            label: t('Add document'),
            multiple: true
          }}
        />
        {isUploadingDocs.map((doc) => (
          <Chip key={`doc-chip-${doc.tempId}`} label={doc} loading={true} />
        ))}
        {documents.length > 0 && <h5>Added documents</h5>}
        {documents.map((doc) => (
          <DocumentChip
            key={`doc-chip-${doc.tempId || doc.id}`}
            label={doc.file?.name || doc.name}
            onClick={(e) => e.preventDefault()}
            onClose={() => popDocument(doc.tempId || doc.id)}
            icon={checkDocumentTypeAndRenderIcon(
              doc.file?.name || doc.name,
              theme
            )}
          />
        ))}
        <Label>Functionality</Label>
        <Checkbox
          label="Support app and property structure"
          checked={compatible.appCompatible}
          onChange={() =>
            setCompatible({
              appCompatible: !compatible.appCompatible,
              propertyStructureCompatible:
                !compatible.propertyStructureCompatible
            })
          }
        />

        <Checkbox
          label="Show Reccurence options for user"
          checked={recurrenceOptions}
          onChange={() => setRecurrenceOptions(!recurrenceOptions)}
        />

        <Checkbox
          label="Show reminder options for user"
          checked={reminderOptions}
          onChange={() => setReminderOptions(!reminderOptions)}
        />

        {recurrenceOptions && (
          <>
            <Label>Recurrences</Label>
            <SubLabel>Inspection interval</SubLabel>
            <Recurrence
              labels={recurrenceLabels}
              unitOptions={recurrenceUnitOptions}
              value={inspectionInterval}
              displayType={displayType}
              onRecurrenceSelect={onInspectionIntervalChange}
              defaultValue={INITIAL_RECURRENCE_VALUE}
            />
          </>
        )}
        {reminderOptions &&
          displayType != RECURRENCE_DISPLAY_TYPES.norepeat && (
            <Recurrence
              labels={{
                legend: 'Reminder before due date',
                number: t('numberOf'),
                unit: t('timeUnits'),
                specify: 'Set reminder',
                norepeat: 'No reminder'
              }}
              unitOptions={recurrenceUnitOptions}
              value={recurrenceWindow}
              displayType={reminderDisplayType}
              defaultValue={INITIAL_RECURRENCE_WINDOW_VALUE}
              onRecurrenceSelect={onInspectionWindowChange}
            />
          )}
      </FormContainer>
      <Footer>
        <Button
          btnType="regular"
          btnStyle="secondary"
          label={t('Cancel')}
          onClick={onClose}
        />
        <Button
          btnType="regular"
          btnStyle="primary"
          label={editMode ? t('Save') : t('Create')}
          onClick={handleSubmit}
          disabled={hasSubmitted}
        />
      </Footer>
    </Wrapper>
  );
};

export default InspectionTypeForm;
